import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";

import ProgressBar from "../../../../../../core/sesionGenerator/components/ProgressBar";
import ComparativaDocentePanel from "../../../../../ComparativaDocentePanel";

const SessionGenerationDialog = ({
  open,
  isGenerating,
  onClose,
  error,
  progress,
  currentMongoStep,
  stepDescriptions,
  stepLabels,
  filaSeleccionada,
  sesionId,
  conComparativa,
  downloading,
  onDownload,
}) => {
  const [openComparativa, setOpenComparativa] = useState(false);
  // En celulares la comparativa ocupa toda la pantalla
  const pantallaCompleta = useMediaQuery((theme) => theme.breakpoints.down("md"));

  /*
   * La comparativa es un paso extra, el último: solo se muestra
   * cuando la sesión se generó a partir de un documento del docente.
   */
  const mostrarComparativa = conComparativa || Boolean(progress?.comparativa);

  const steps = [
    "mongo",
    "word",
    "pdf",
    "html",
    ...(mostrarComparativa ? ["comparativa"] : []),
  ];

  const estadoDe = (step) => progress?.[step]?.estado;

  // Se puede abrir al terminar o fallar el paso (desde la vista se reintenta),
  // o con un progreso guardado antes de existir este paso
  const comparativaLista =
    ["completado", "error"].includes(estadoDe("comparativa")) ||
    (!isGenerating && estadoDe("html") === "completado");

  return (
    <>
      <Dialog
        open={open}
        onClose={() => {
          if (!isGenerating) onClose();
        }}
        disableEscapeKeyDown={isGenerating}
        maxWidth="md"
        fullWidth
        className="step7-dialog"
      >
        <DialogTitle className="step7-dialog-title" sx={{ pb: 1 }}>
          <Typography component="div" variant="subtitle2" color="text.secondary">
            Proceso de generación
          </Typography>

          {isGenerating && (
            <Typography component="div" variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              ⏱️ Este proceso puede tardar aproximadamente 2 minutos. Por favor, no cierres esta ventana.
            </Typography>
          )}

          <Typography component="div" variant="h6" sx={{ fontWeight: 600 }}>
            {filaSeleccionada?.tema || "Tema..."}
          </Typography>
        </DialogTitle>

        <DialogContent className="step7-dialog-content">
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {progress?.mongo?.estado === "en_progreso" && currentMongoStep && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {stepDescriptions[currentMongoStep] ?? "Procesando información…"}
            </Typography>
          )}

          {steps.map((step) => {
            const stepData = progress?.[step] ?? {};
            return (
              <ProgressBar
                key={step}
                label={stepLabels[step]}
                estado={stepData.estado}
                progreso={stepData.progreso ?? 0}
                tiempo={stepData.tiempo ?? 0}
              />
            );
          })}

          {estadoDe("comparativa") === "error" && (
            <Alert severity="warning">
              No se pudo generar la comparativa. La sesión y sus archivos están listos;
              puedes reintentarla desde «Ver comparativa».
            </Alert>
          )}
        </DialogContent>

        <DialogActions className="step7-dialog-actions">
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={isGenerating}
            className="step7-btn step7-btn-back step7-btn-close"
          >
            Cerrar
          </Button>

          {/* Cada descarga se habilita en cuanto termina su paso */}
          {["word", "pdf", "html"].map((type) => {
            const listo = estadoDe(type) === "completado";
            return (
              <Tooltip key={type} title={!listo ? "Esperando a que termine este paso" : ""}>
                <span className={`step7-dl-wrap step7-dl-${type}`}>
                  <Button
                    variant="contained"
                    disabled={!listo || downloading[type]}
                    onClick={() => onDownload(type)}
                    startIcon={downloading[type] ? <CircularProgress size={16} color="inherit" /> : null}
                    className={`step7-btn step7-btn-download step7-btn-${type}`}
                  >
                    {downloading[type] ? `Descargando ${type.toUpperCase()}` : `Descargar ${type.toUpperCase()}`}
                  </Button>
                </span>
              </Tooltip>
            );
          })}

          {mostrarComparativa && (
            <Tooltip title={!comparativaLista ? "Esperando a que termine este paso" : ""}>
              <span className="step7-dl-wrap step7-dl-comparativa">
                <Button
                  variant="contained"
                  color="secondary"
                  disabled={!comparativaLista || !sesionId}
                  onClick={() => setOpenComparativa(true)}
                  className="step7-btn step7-btn-comparativa"
                >
                  Ver comparativa
                </Button>
              </span>
            </Tooltip>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={openComparativa}
        onClose={() => setOpenComparativa(false)}
        maxWidth="xl"
        fullWidth
        fullScreen={pantallaCompleta}
        PaperProps={{
          sx: pantallaCompleta
            ? { overflow: "hidden" }
            : { height: "92vh", borderRadius: 3, overflow: "hidden" },
        }}
      >
        <Box sx={{ height: "100%", minHeight: 0 }}>
          {openComparativa && (
            <ComparativaDocentePanel
              sesionId={sesionId}
              nombreSesion={filaSeleccionada?.tema}
              onClose={() => setOpenComparativa(false)}
            />
          )}
        </Box>
      </Dialog>
    </>
  );
};

export default SessionGenerationDialog;
